import { supabase } from './supabase';

function normalizeText(text: string): string {
  return text
    .toLowerCase()
    .replace(/\s+/g, ' ')  // Replace multiple spaces with single space
    .replace(/[^\w\s]/g, '') // Remove special characters
    .trim();
}

export async function searchInsights(query: string, projectId?: string) {
  try {
    // Normalize the search query
    const normalizedQuery = normalizeText(query);
    
    // Generate embedding using our Edge Function
    const { data: embeddingData, error: embeddingError } = await supabase.functions.invoke('generate-embedding', {
      body: { text: normalizedQuery }
    });

    if (embeddingError) {
      console.error('Embedding generation error:', embeddingError);
      throw new Error(embeddingError.message || 'Failed to generate embedding');
    }

    if (!embeddingData?.embedding) {
      throw new Error(embeddingData?.error || 'No embedding was generated for the search query');
    }

    // Get insights with similarity scores and joined data
    const { data, error: matchError } = await supabase
      .rpc('match_insights', {
        query_embedding: embeddingData.embedding,
        match_threshold: 0.4, // Lowered threshold for more results
        match_count: 10
      });

    if (matchError) {
      console.error('Insight matching error:', matchError);
      throw new Error(matchError.message || 'Failed to search insights');
    }

    if (!data) {
      return [];
    }

    // Filter by project if needed
    const filteredInsights = projectId
      ? data.filter((insight: any) => insight.project_id === projectId)
      : data;

    // Transform the data to match the expected structure
    return filteredInsights.map((insight: any) => ({
      ...insight,
      interview: {
        customer: {
          full_name: insight.customer_name
        },
        project: {
          title: insight.project_title
        }
      }
    }));
  } catch (error) {
    console.error('Error searching insights:', error);
    throw error instanceof Error 
      ? error 
      : new Error('An unexpected error occurred while searching insights');
  }
}

export async function createInsight(
  interviewId: string,
  title: string,
  description: string,
  startTimestamp: string,
  endTimestamp?: string
) {
  try {
    // Normalize the text before generating embedding
    const normalizedText = normalizeText(`${title} ${description}`);
    
    // Generate embedding using Edge Function
    const { data: embeddingData, error: embeddingError } = await supabase.functions.invoke('generate-embedding', {
      body: { text: normalizedText }
    });

    if (embeddingError) {
      console.error('Embedding generation error:', embeddingError);
      throw new Error(embeddingError.message || 'Failed to generate embedding');
    }

    if (!embeddingData?.embedding) {
      throw new Error(embeddingData?.error || 'No embedding was generated for the insight');
    }

    const { data, error: insertError } = await supabase
      .from('insights')
      .insert({
        interview_id: interviewId,
        title,
        description,
        start_timestamp: startTimestamp,
        end_timestamp: endTimestamp,
        embedding: embeddingData.embedding
      })
      .select()
      .single();

    if (insertError) {
      console.error('Insight creation error:', insertError);
      throw new Error(insertError.message || 'Failed to create insight');
    }

    return data;
  } catch (error) {
    console.error('Error creating insight:', error);
    throw error instanceof Error 
      ? error 
      : new Error('An unexpected error occurred while creating insight');
  }
}